/**
 * Rehire review queue types. Mirrors backend {@code RehireReviewCandidateResponse}
 * and {@code RehireReviewDecideRequest} DTOs from the jlm module.
 */

import type {Instant, UUID} from './common'

/* eslint-disable i18next/no-literal-string -- const definitions are the source of truth */

export const RehireReviewStatus = {
    PENDING: 'PENDING',
    RESURRECTED: 'RESURRECTED',
    CREATED_NEW: 'CREATED_NEW',
    REJECTED: 'REJECTED',
} as const
export type RehireReviewStatus = (typeof RehireReviewStatus)[keyof typeof RehireReviewStatus]

export const RehireReviewDecision = {
    RESURRECTED: 'RESURRECTED',
    CREATED_NEW: 'CREATED_NEW',
    REJECTED: 'REJECTED',
} as const
export type RehireReviewDecision = (typeof RehireReviewDecision)[keyof typeof RehireReviewDecision]

/**
 * Polymorphic actor type. A rehire-review candidate may be decided by a human
 * {@code USER} (via the review UI) or automatically by a {@code CONNECTOR}
 * (fast-path auto-resurrect).
 */
export const RehireDecisionActorType = {
    USER: 'USER',
    CONNECTOR: 'CONNECTOR',
} as const
export type RehireDecisionActorType = (typeof RehireDecisionActorType)[keyof typeof RehireDecisionActorType]

/* eslint-enable i18next/no-literal-string */

export interface RehireReviewCandidate {
    id: UUID
    matchedIdentityId: UUID
    matchedIdentityDisplayName: string | null
    sourceConnectorId: UUID
    sourceConnectorDisplayName: string | null
    sourceSyncRunId: UUID
    incomingRecordSnapshot: Record<string, unknown>
    newEntryDate: string | null
    detectedAt: Instant
    status: RehireReviewStatus
    decidedByActorId: UUID | null
    decidedByActorType: RehireDecisionActorType | null
    decidedByConnectorName: string | null
    decidedAt: Instant | null
    decisionComment: string | null
    newIdentityId: UUID | null
    version: number
}

export interface RehireReviewDecideRequest {
    decision: RehireReviewDecision
    reason: string
    /**
     * ISO-8601 local date (YYYY-MM-DD). Required when the candidate has no stored
     * newEntryDate (inbound snapshot had no hireDate) or the admin overrides it.
     * Ignored for REJECTED decisions.
     */
    newEntryDate?: string | null
}

export interface RehireReviewDecideResponse {
    id: UUID
    status: RehireReviewStatus
    newIdentityId: UUID | null
    matchedIdentityId: UUID
}

export interface RehireReviewCountResponse {
    count: number
    status: RehireReviewStatus
}

/**
 * Previous access profile assigned to the rehire candidate's matched identity.
 * Mirrors backend {@code PreviousAccessProfileResponse}; shape matches
 * {@code PeerAccessProfile} so the existing PeerRecommendationList component can render it.
 */
export interface PreviousAccessProfile {
    accessProfileId: UUID
    accessProfileName: string
    resourceName: string
    environment: string
    expiryRequired: boolean
    maxDurationDays: number | null
    peerCount: number
}
